import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, makeStateKey, PLATFORM_ID, Renderer2, RendererFactory2, TransferState } from '@angular/core';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import { Title } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

export interface PageMetadata {
  title: string;
  description: string;
  keywords: string[];
  type: string;
  ogTitle?: string;
  ogType?: string;
  ogUrl?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalLink?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  schema?: any[]; // Add schema support
}

const defaultMetadata: PageMetadata = {
  title: 'Irbe7 - Bourse de Tunis',
  description: 'Actualités financières et boursières en Tunisie',
  keywords: ['Bourse Tunis', 'Finance Tunisie', 'Marché Actions'],
  type: 'website',
};
const SCHEMA_STATE_KEY = makeStateKey<string>('schema');

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private renderer: Renderer2;
  private isBrowser: boolean;

  private metaMappings = {
    title: { name: 'title' },
    description: { name: 'description' },
    keywords: { name: 'keywords' },
    auteur: { name: 'author' },
    section: { property: 'article:section' },
    ogTitle: { property: 'og:title' },
    ogType: { property: 'og:type' },
    ogUrl: { property: 'og:url' },
    ogDescription: { property: 'og:description' },
    ogImage: { property: 'og:image' },
    publishedTime: { property: 'article:published_time' },
    modifiedTime: { property: 'article:modified_time' },
    twitterTitle: { name: 'twitter:title' },
    twitterDescription: { name: 'twitter:description' },
    twitterImage: { name: 'twitter:image' },
  };

  constructor(
    private meta: Meta,
    private titleService: Title,
    rendererFactory: RendererFactory2,
    private sanitizer: DomSanitizer,    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.platformId = platformId;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Update meta tags and JSON-LD schema dynamically
   */
  updateMetaTags(metaData: {
    title?: string;
    description?: string;
    keywords?: string;
    auteur?: string;
    section?: string;
    ogTitle?: string;
    ogType?: string;
    ogUrl?: string;
    ogDescription?: string;
    ogImage?: string;
    publishedTime?: string;
    modifiedTime?: string;
    twitterTitle?: string;
    twitterDescription?: string;
    twitterImage?: string;
    canonicalLink?: string;
  }){
    // Update standard meta tags
    Object.keys(this.metaMappings).forEach((key) => {
      const tagSelector = this.metaMappings[key];
      const content = metaData[key];
      if (content) {
        this.updateOrAddMetaTag(tagSelector, content);
      } else {
        this.clearMetaTag(tagSelector);
      }
    });

    // Handle page title
    if (metaData.title) {
      this.titleService.setTitle(metaData.title);
    } else {
      this.titleService.setTitle(defaultMetadata.title);
    }

    // Handle canonical link
    if (metaData.canonicalLink) {
      this.updateCanonicalLink(metaData.canonicalLink);
    } else {
      this.clearCanonicalLink();
    }
  }

  /**
   * Add or update a specific meta tag
   */
  private updateOrAddMetaTag(selector: { name?: string; property?: string }, content: string) {
    const attribute = selector.name ? 'name' : 'property';
    const value = selector.name || selector.property;
    const existingTag = this.meta.getTag(`${attribute}="${value}"`);

    if (existingTag) {
      this.meta.updateTag({ ...selector, content });
    } else {
      this.meta.addTag({ ...selector, content });
    }
  }

  /**
   * Remove a specific meta tag
   */
  private clearMetaTag(selector: { name?: string; property?: string }) {
    const attribute = selector.name ? 'name' : 'property';
    const value = selector.name || selector.property;

    const existingTag = this.meta.getTag(`${attribute}="${value}"`);
    if (existingTag) {
      this.meta.removeTag(`${attribute}="${value}"`);
    }
  }

  /**
   * Update the canonical link dynamically
   */
  private updateCanonicalLink(href: string) {
    if (this.isBrowser) {
      let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = this.renderer.createElement('link');
        this.renderer.setAttribute(link, 'rel', 'canonical');
        this.renderer.appendChild(document.head, link);
      }
      this.renderer.setAttribute(link, 'href', href);
    }
  }

  private clearCanonicalLink() {
    if (this.isBrowser) {
      const existingLink = document.querySelector('link[rel="canonical"]');
      if (existingLink) {
        this.renderer.removeChild(document.head, existingLink);
      }
    }
  }
  updateFinancialMetaTags(financialData: {
    stockSymbol?: string;
    stockExchange?: string;
    stockSector?: string;
    stockCurrency?: string;
    stockIsin?: string;
    stockLastUpdated?: string;
  }) {
    const financialMappings = {
      stockSymbol: { name: 'stock:symbol' },
      stockExchange: { name: 'stock:exchange' },
      stockSector: { name: 'stock:sector' },
      stockCurrency: { name: 'stock:currency' },
      stockIsin: { name: 'stock:isin' },
      stockLastUpdated: { name: 'stock:lastUpdated' },
    };
  
    Object.keys(financialMappings).forEach((key) => {
      const tagSelector = financialMappings[key];
      const content = financialData[key];
      if (content) {
        this.updateOrAddMetaTag(tagSelector, content);
      } else {
        this.clearMetaTag(tagSelector);
      }
    });
  }
  
/**
   * Set JSON-LD schema that will be included in the initial HTML
   */
  setSchema(schema: any | any[]): void {
    const schemas = Array.isArray(schema) ? schema : [schema];
    const schemaJson = JSON.stringify(schemas);
    
    // Store in transfer state to avoid duplication during hydration
    this.transferState.set(SCHEMA_STATE_KEY, schemaJson);
    
    // If we're on the server, we don't need to do anything else
    // The app.component.html will read from transferState
  }

  /**
   * Generate ItemList schema for articles and news
   */
  generateItemListSchema(
    baseUrl: string,
    articles: any[] = [],
    news: any[] = [],
    infographics: any[] = []
  ): any {
    // Combine all items
    const allItems = [...articles, ...news, ...infographics];
    
    // Create list items
    const itemListElements = allItems.map((item, index) => ({
      "@type": "ListItem",
      "position": (index + 1).toString(),
      "url": `${baseUrl}${item.url || item.slug || ''}`,
      "name": item.title
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": baseUrl
      },
      "url": baseUrl,
      "numberOfItems": itemListElements.length.toString(),
      "itemListOrder": "https://schema.org/ItemListOrderAscending",
      "itemListElement": itemListElements
    };
  }

  /**
   * Generate SiteNavigationElement schema
   */
  generateSiteNavigationSchema(navigationItems: { name: string; url: string }[]): any[] {
    return navigationItems.map(item => ({
      "@context": "https://schema.org",
      "@type": "SiteNavigationElement",
      "name": item.name,
      "url": item.url
    }));
  }
}